import { useState, useEffect } from 'react';
import { resolveImagePath } from '@/utils/imageResolver';
import heroSlidesData from '@/data/heroSlides.json';

interface HeroSlide {
  id: string;
  title: string;
  description: string;
  backgroundImage: string;
  foodImage: string;
  price: string;
  category: string;
}

export function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  
  const slides: HeroSlide[] = heroSlidesData;

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [slides.length, isAutoPlaying]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 10 seconds of manual interaction
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <div className="relative h-[600px] w-full overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center transition-all duration-1000 ease-in-out"
        style={{
          backgroundImage: `url(${resolveImagePath(currentSlideData.backgroundImage)})`,
        }}
      >
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/40" />
      </div>

      {/* Content Container */}
      <div className="relative h-full flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            
            {/* Left Content */}
            <div className="text-white space-y-6 z-10">
              <div className="inline-block px-3 py-1 bg-amber-600 text-sm font-semibold rounded-full text-white">
                {currentSlideData.category}
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                {currentSlideData.title}
              </h1>
              
              <p className="text-lg md:text-xl text-gray-200 max-w-lg leading-relaxed">
                {currentSlideData.description}
              </p>
              
              <div className="flex items-center space-x-4">
                <span className="text-3xl font-bold text-amber-400">
                  {currentSlideData.price}
                </span>
                <button className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-3 rounded-full font-semibold transition-colors duration-300 inline-flex items-center space-x-2">
                  <span>Read More</span>
                  <span>→</span>
                </button>
              </div>
            </div>

            {/* Right Content - Food Image */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <img
                  src={resolveImagePath(currentSlideData.foodImage)}
                  alt={currentSlideData.title}
                  className="w-80 h-80 object-cover rounded-full shadow-2xl border-4 border-white/20 transition-transform duration-1000 ease-in-out hover:scale-105"
                />
                {/* Decorative elements */}
                <div className="absolute -top-4 -right-4 w-8 h-8 bg-amber-400 rounded-full animate-pulse" />
                <div className="absolute -bottom-6 -left-6 w-12 h-12 bg-amber-600 rounded-full opacity-60 animate-bounce" />
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Navigation Dots */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
        <div className="flex space-x-3">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-4 h-4 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-amber-400 scale-125'
                  : 'bg-white/50 hover:bg-white/70'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Arrow Navigation */}
      <button
        onClick={() => goToSlide(currentSlide > 0 ? currentSlide - 1 : slides.length - 1)}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300"
        aria-label="Previous slide"
      >
        ←
      </button>
      
      <button
        onClick={() => goToSlide((currentSlide + 1) % slides.length)}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300"
        aria-label="Next slide"
      >
        →
      </button>
    </div>
  );
}